import {
  getOhCookie,
  setOhCookies,
  getNumberTypeCookie,
} from '../../../../shared/cookie';
// import { trackPageView, trackRetention } from './analytics';
import { trackPageView } from './analytics';
import { createUserId, timeInMs, toDayFormat } from '../../../../shared';
import { syncRemoteConfig } from '../../../../shared/user';

import {
  // trackedRetention,
  updatedUser,
  createdUser,
  userSlice,
  fetchedUserId,
  setInstallSource,
  setUtmCampaign,
} from './reducer';
import {
  selectAuthToken,
  selectTotalShellsCounter,
} from './selectors';
import { selectFeatureFlags } from '../../../store/selectors';
import { GlobalState, Thunk } from '../../../types.global';

import * as actions from './actions';
import { selectBanner } from '../../banner/slice';
import { selectBackgroundPicture, updateRawPictureUrl } from '../../ui/slice';
import { requestFileSystem } from '../../../../shared/chromeApi';

const checkIsLogged = (account) => {
  // Six different parameters are different so it's guessing which one to use
  // logged
  // { 5: 1, 6: 1, 7: 0, 9: 1, 14: null, 15: 1 }
  // Not logged
  //   { 5: 0, 6: null, 7: null, 9: 0, 14: 1 }

  return account[15];
};

export const fetchGoogleAccounts = () => async (dispatch) => {
  /*
    await new Promise((resolve, reject) => {
      chrome.permissions.contains(
        {
          permissions: ['cookies'],
          origins: ['https://accounts.google.com'],
        },
        (result) => (result ? resolve(result) : reject())
      );
    });
     */

  const cookies: chrome.cookies.Cookie[] = await new Promise((resolve) => {
    window.chrome.cookies.getAll(
      { url: 'https://accounts.google.com' },
      resolve
    );
  });

  if (!cookies.length) {
    return dispatch(actions.fetchGoogleAccounts.fulfilled(null));
  }
  // if (!(cookies || []).find(({ name }) => name === 'SAPISID')) return null;

  const result = await fetch(
    `https://accounts.google.com/ListAccounts?${new URLSearchParams({
      json: 'standard',
      source: 'ogb',
      md: '1',
      cc: '1',
      mn: '1',
      mo: '1',
      gpsia: '1',
      listPages: '1',
      authuser: '0',
      origin: 'https://www.google.com',
      fwput: '226',
    })}`
  );

  if (result.status === 200) {
    const text = await result.text();
    const str = text
      .split(`postMessage('`)[1]
      .split(`', 'https`)[0]
      .replace(/\\x([0-9A-Fa-f]{2})/g, (...args) =>
        String.fromCharCode(parseInt(args[1], 16))
      )
      .replace(/\\n/g, '');

    const parsed = JSON.parse(str);
    dispatch(actions.fetchGoogleAccounts.fulfilled(parsed[1].map((el) => ({
      name: el[2],
      email: el[3],
      pictureUrl: el[4],
      isLogged: checkIsLogged(el),
    }))));

    return;
  }

  dispatch(actions.fetchGoogleAccounts.fulfilled(null));
};

const isWithinLast24hrs = (date) => Date.now() - Number(new Date(date)) > timeInMs.day;
const selectLastViewedPopover = (state) => state.friends.lastViewed;

// Update counter
export const fetchLoggedUser = (): Thunk => async (dispatch, getState) => {
  const state = getState();
  const now = Date.now();

  const { loggedUser, shells } = state.user;
  const previouslyOpenedAt = state.user.previouslyOpenedAt || 0;
  let token = loggedUser.token;

  if (token) {
    setOhCookies('authorization', token);
  } else {
    token = await getOhCookie('authorization');

    if (!token) {
      dispatch(actions.fetchLoggedUser.fulfilled({ data: false, fetchedAt: now }));
      return;
    }
  }

  const timeout = (() => {
    try {
      if (!loggedUser.value) return timeInMs.second * 15;

      // Someone who signed up NOT within last 24hrs
      if (now - Number(new Date(loggedUser.value.createdAt)) > timeInMs.day) {
        // if someone does not have active friends
        const friends = state.friends.friends.list || [];
        if (!friends.find(({ updatedAt }) => isWithinLast24hrs(updatedAt))) {
          const lastViewedPopover = selectLastViewedPopover(state) || 0;
          if (now - lastViewedPopover > timeInMs.hour) {
            return timeInMs.hour;
          }
        }
      }
    } catch (err) {
      console.error('timeout', err);
    }

    return timeInMs.second * 15;
  })();

  if (now - previouslyOpenedAt < timeout) {
    dispatch(actions.fetchLoggedUser.fulfilled(null));
    return;
  }

  const res = await fetch(
    `${process.env.APP_URL}/api/friends?${new URLSearchParams({
      shellsCounter: `${shells.shellsCounter}`,
      version: process.env.APP_VERSION,
    })}`,
    {
      headers: { authorization: token },
    }
  );

  if (res.status !== 200) {
    const error = await res.text();
    console.error('fetchUserAndFriends', { error, status: res.status });
    dispatch(actions.fetchLoggedUser.rejected(null));
    return;
  }

  const json = await res.json();
  dispatch(actions.fetchLoggedUser.fulfilled({ data: json, token, fetchedAt: now }));
};

export const updateUser = (data: Record<string, string | number>): Thunk => async (dispatch, getState) => {
  const state = getState();
  const token = selectAuthToken(state);

  const res = await fetch(`${process.env.APP_URL}/api/users`, {
    method: 'PUT',
    headers: { authorization: token, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (res.status !== 200) {
    const message = await res.json();
    const formatted = { message, status: res.status };
    console.error('fetchUserAndFriends', formatted);
    throw formatted;
  }

  const user = await res.json();
  dispatch(updatedUser(user));
};

const getNewUserCounters = (user) => {
  return {
    shellsCounter: user.shells.shellsCounter,
  };
};

export const createUser =
  (data: Record<string, string | number>): Thunk =>
    async (dispatch, getState) => {
      const state = getState();
      const res = await fetch(`${process.env.APP_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, ...getNewUserCounters(state.user) }),
      });

      if (res.status !== 200) {
        const message = await res.json();
        const formatted = { message, status: res.status };
        console.error('fetchUserAndFriends', formatted);
        throw formatted;
      }

      const json = await res.json();
      dispatch(createdUser({ ...json, fetchedAt: Date.now() }));
      dispatch(fetchLoggedUser());
    };

export const incrementShells = (gain: number, source?: string): Thunk => async (dispatch, getState) => {
  const cookieCounter = await getNumberTypeCookie('shellsCounter');

  const state = getState();
  const localCounter = selectTotalShellsCounter(state);
  const highestCounter = Math.max(cookieCounter || 0, localCounter);

  const newCounter = highestCounter + gain;
  setOhCookies('shellsCounter', newCounter);
  return dispatch(
    userSlice.actions.gainedShells({
      value: newCounter,
      updatedAt: Date.now(),
      gain,
      source,
    })
  );
};

const DIMINISHING_RETURNS_CONFIG_DEFAULT = [
  [10, 10],
  [30, 3],
  [null, 1],
];

const getGainedShells = (state: GlobalState): [number, string] => {
  const featureFlags = selectFeatureFlags(state);
  const { user } = state;

  if (user.shells.boosterUsed) return [50, 'booster'];

  const value = (() => {
    // TODO
    // if (user.quickReload < 0) return 0;

    if (!featureFlags.DIMINISHING_RETURNS)
      return DIMINISHING_RETURNS_CONFIG_DEFAULT[0][1];

    const config =
      featureFlags.DIMINISHING_RETURNS_CONFIG ||
      DIMINISHING_RETURNS_CONFIG_DEFAULT;

    const found = config.find(
      ([limit]) => user.ntpDayVisitCounter <= limit || !limit
    );
    return found
      ? found[1]
      : DIMINISHING_RETURNS_CONFIG_DEFAULT[
      DIMINISHING_RETURNS_CONFIG_DEFAULT.length - 1
      ][1];
  })();

  const extra = selectBanner(state) ? 10 : 0;

  return [value + extra, 'tabOpens'];
};

const checkUserId = async ({ user }) => {
  if (user.userId) return null;

  const userId = await getOhCookie('OH_USER_ID').catch(() => null);

  return userId || createUserId();
};

const handleInstalledCookies = ({ user }) => {
  const date = new Date(user.installationDate);
  const extensionInstallDate = toDayFormat(date).split('-').join('');
  [
    ['OH_USER_ID', user.userId],
    [
      'Extension_User',
      `${process.env.APP_PLATFORM}_${process.env.APP_TYPE}_${extensionInstallDate}`,
    ],
    ['Extension_Install_Date', extensionInstallDate],
  ].map(([name, value]) => setOhCookies(name, value));
};

const processCustomPic = (): Thunk => async (dispatch) => {
  try {
    const fs = await requestFileSystem();
    const reader = fs.root.createReader();
    const entries = await new Promise < FileSystemEntry[] > ((resolve) => reader.readEntries(resolve));
    if (entries?.length) {
      const url = entries[0].toURL();
      dispatch(updateRawPictureUrl(url));
      return;
    }
    // Fallback to bundled image
    const fallback = `${process.env.APP_PLATFORM === 'extension' ? '../' : ''}img/background/fallback_ocean.webp`;
    dispatch(updateRawPictureUrl(fallback));
  } catch (err) {
    console.error('loading custom image failed', err);
  }
};

export const checkRegularUser = () => async (dispatch) => {
  const cookie = await getOhCookie("installSource");

  if (cookie) {
    dispatch(setInstallSource(cookie));
  }
};

export const checkUtmCampaign = () => async (dispatch) => {
  const result = await getOhCookie('utm_campaign');
  if (!result) {
    dispatch(setUtmCampaign(false))
  } else {
    dispatch(setUtmCampaign(true))
  }
}

export const openedTab = (): Thunk => async (dispatch, getState) => {
  if (process.env.APP_PLATFORM === 'extension' && process.env.APP_TYPE === 'lite') {
    dispatch(fetchGoogleAccounts());
    dispatch(processCustomPic());
  }

  dispatch(checkRegularUser());

  const newUserId = await checkUserId(getState());
  if (newUserId) dispatch(fetchedUserId(newUserId));

  const state = getState();
  handleInstalledCookies(state);
  const [gain, source] = getGainedShells(state);
  if (gain) {
    await dispatch(incrementShells(gain, source));
  }
  dispatch(fetchLoggedUser());

  dispatch(checkUtmCampaign());

  try {
    const { fetchedAt } = state.user.experiment;
    const now = Date.now();

    if (!fetchedAt || fetchedAt < now - timeInMs.minute * 60) {
      const experiment = await syncRemoteConfig({
        experiment: state.user.experiment.value,
        installationDate: state.user.installationDate,
      });
      if (experiment) {
        dispatch(
          userSlice.actions.fetchedExperiment({
            value: experiment,
            fetchedAt: now,
          })
        );
      }
    }
  } catch (err) {
    console.error('syncRemoteConfig:error', err);
  }

  // passing function because of potential time issue with BROWSER_STORAGE_UPDATE
  trackPageView(getState)
    .then(() => {
      // const state = getState() as GlobalState;
      // if (trackRetention(state)) {
      //   dispatch(trackedRetention({ when: Date.now() }));
      // }
    })
    .catch((err) => {
      console.error('trackPageView', err);
    });
};
